/**
 * The five interaction sub-forms that have fields to author. `audio_response` carries no fields at
 * all, so it has no hook — its component is one honest sentence.
 *
 * THE RULE THAT SHAPES ALL OF THIS (B9): none of these edits the document; each is a thin wrapper
 * over a store action. In particular the radio semantics of a single-answer multiple choice live
 * IN THE STORE — `updateChoice(…, { correct: true })` clears the other options, and multi → single
 * keeps the first correct one (T09). Re-implementing that here would give two answers to one
 * question, and the save would use the store's.
 *
 * The owning ids and the interaction arrive as GETTERS: the ids change when another activity is
 * selected, and the interaction object is replaced wholesale by `setInteractionType`.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { Choice, MultipleChoice } from './interfaces'
import type { Interaction, InteractionType, OpenEndedFormat } from './types'

import { fromInlineText, toInlineText } from '../../lib/assignments/fill-in-blanks'
import { useAssessmentEditorStore } from './store'
import { DEFAULT_OPEN_ENDED_FORMATS, DRAG_GAP_REGEX, OPEN_ENDED_FORMATS } from '../../lib/assessments/schema'

/** The schema's `options: z.array(Choice).min(2)`. Below this the document is invalid, so the
 *  remove button is disabled rather than letting the author walk into a save error. */
const MIN_CHOICES = 2

/** One marker's content, in authored order — the answer key spelled out under the marker counter,
 *  so the author can read what they marked without re-scanning the sentence. */
interface MarkedWordItem {
  key: string
  position: number
  value: string
}

/**
 * `match` on the schema's own marker regex — the one the counter counts with — so a list can never
 * disagree with the "N …" line above it; `match` also ignores the global flag's `lastIndex`. Each
 * match keeps its delimiters, hence the `slice`: `delimiter` is their length, 1 for `*…*` and 2
 * for `{{…}}`.
 */
const markedWords = (text: string, regex: RegExp, delimiter: number): MarkedWordItem[] =>
  (text.match(regex) ?? []).map((match, index) => ({
    key: `word-${index}`,
    position: index + 1,
    value: match.slice(delimiter, -delimiter).trim(),
  }))

/**
 * Every interaction editor takes the same three props, which is what makes `INTERACTION_EDITORS` a
 * legitimate `Record<InteractionType, Component>`.
 */
export interface InteractionEditorProps {
  screenId: string
  activityId: string
  interaction: Interaction
}

interface UseInteractionEditorOptions {
  screenId: () => string
  activityId: () => string
  interaction: () => Interaction
}

/** Narrows the activity's interaction to one member of the union. `null` when the component was
 *  handed a different type — impossible through the registry, but the type system cannot know it,
 *  and a no-op beats a cast that lies. */
const narrow = <T extends InteractionType>(
  interaction: Interaction,
  type: T,
): Extract<Interaction, { type: T }> | null => (
  interaction.type === type ? interaction as Extract<Interaction, { type: T }> : null
)

// --- multiple_choice -------------------------------------------------------

interface ChoiceItem {
  id: string
  text: string
  correct: boolean
  isFirstInOrder: boolean
  isLastInOrder: boolean
}

interface UseInteractionMultipleChoiceReturn {
  refMode: ComputedRef<MultipleChoice['mode']>
  refModeOptions: ComputedRef<NativeSelectOption[]>
  refModeHint: ComputedRef<string>
  refChoices: ComputedRef<ChoiceItem[]>
  refCanRemoveChoice: ComputedRef<boolean>
  setMode: (value: string) => void
  addChoice: () => void
  removeChoice: (choiceId: string) => void
  moveChoiceUp: (choiceId: string) => void
  moveChoiceDown: (choiceId: string) => void
  setChoiceText: (choiceId: string, value: string) => void
  toggleChoiceCorrect: (choiceId: string, current: boolean) => void
}

const MODE_LABELS: Record<MultipleChoice['mode'], string> = {
  single: 'Single answer',
  multi: 'Several answers',
}

/** The mode is shown, and explained, because it changes what the correct-toggles DO — a toggle
 *  that silently unticks its neighbours only surprises when the mode is invisible. */
const MODE_HINTS: Record<MultipleChoice['mode'], string> = {
  single: 'Exactly one option is correct — marking one clears the others.',
  multi: 'Any number of options may be correct.',
}

export const useInteractionMultipleChoice = (
  { screenId, activityId, interaction }: UseInteractionEditorOptions,
): UseInteractionMultipleChoiceReturn => {
  const store = useAssessmentEditorStore()

  const refInteraction = computed(() => narrow(interaction(), 'multiple_choice'))

  const refMode = computed<MultipleChoice['mode']>(() => refInteraction.value?.mode ?? 'single')

  const refModeOptions = computed<NativeSelectOption[]>(() =>
    (Object.keys(MODE_LABELS) as MultipleChoice['mode'][]).map(value => ({
      id: value,
      value,
      text: MODE_LABELS[value],
      selected: value === refMode.value,
    })),
  )

  const refModeHint = computed<string>(() => MODE_HINTS[refMode.value])

  const refOptions = computed<Choice[]>(() => refInteraction.value?.options ?? [])

  const refChoices = computed<ChoiceItem[]>(() =>
    refOptions.value.map((choice, index) => ({
      id: choice.id,
      text: choice.text,
      correct: choice.correct,
      isFirstInOrder: index === 0,
      isLastInOrder: index === refOptions.value.length - 1,
    })),
  )

  const refCanRemoveChoice = computed<boolean>(() => refOptions.value.length > MIN_CHOICES)

  /** Mode is the one discriminant-adjacent field with a store rule of its own: `multi` → `single`
   *  keeps the first correct option and clears the rest, in the store (T09). */
  const setMode = (value: string): void => {
    store.updateMultipleChoice(screenId(), activityId(), {
      mode: value as MultipleChoice['mode'],
    })
  }

  const addChoice = (): void => {
    store.addChoice(screenId(), activityId())
  }

  const removeChoice = (choiceId: string): void => {
    store.removeChoice(screenId(), activityId(), choiceId)
  }

  const moveChoiceUp = (choiceId: string): void => {
    store.moveChoiceUp(screenId(), activityId(), choiceId)
  }

  const moveChoiceDown = (choiceId: string): void => {
    store.moveChoiceDown(screenId(), activityId(), choiceId)
  }

  const setChoiceText = (choiceId: string, value: string): void => {
    store.updateChoice(screenId(), activityId(), choiceId, { text: value })
  }

  /** Sends the flipped value and renders what comes back — under `mode: 'single'` the store clears
   *  the other options, which is the whole reason this is not done locally. */
  const toggleChoiceCorrect = (choiceId: string, current: boolean): void => {
    store.updateChoice(screenId(), activityId(), choiceId, { correct: !current })
  }

  return {
    refMode,
    refModeOptions,
    refModeHint,
    refChoices,
    refCanRemoveChoice,
    setMode,
    addChoice,
    removeChoice,
    moveChoiceUp,
    moveChoiceDown,
    setChoiceText,
    toggleChoiceCorrect,
  }
}

// --- fill_in_blanks --------------------------------------------------------

/**
 * ONE field, because there is only one thing to author: the sentence, with each answer written
 * inside its own braces (`7 + {{five}} = {{twelve}}`).
 *
 * The old surface edited a `blanks` array matched to gaps by index, with a counter and a mismatch
 * warning to compensate for nothing on screen saying which gap `blanks[2]` answered. Now the answer
 * lives in the gap, `blanks` is derived, and re-ordering gaps is re-ordering the sentence.
 */
interface UseInteractionFillInBlanksReturn {
  /** The INLINE form — gaps carry their answers. Not `interaction.text`, which is stored form. */
  refInlineText: ComputedRef<string>
  setInlineText: (value: string) => void
}

export const useInteractionFillInBlanks = (
  { screenId, activityId, interaction }: UseInteractionEditorOptions,
): UseInteractionFillInBlanksReturn => {
  const store = useAssessmentEditorStore()

  const refInteraction = computed(() => narrow(interaction(), 'fill_in_blanks'))

  const refInlineText = computed<string>(() => toInlineText(
    refInteraction.value?.text ?? '',
    refInteraction.value?.blanks ?? [],
  ))

  /**
   * Text and blanks are written in ONE patch, from one parse: two calls would leave the document
   * momentarily mismatched, and the validation report reads the document, not the keystroke. This
   * runs per keystroke, and the codec guarantees `toInlineText(fromInlineText(s)) === s`, so the
   * value coming back down is identical and the caret stays put.
   */
  const setInlineText = (value: string): void => {
    store.updateFillInBlanks(screenId(), activityId(), fromInlineText(value))
  }

  // No `caseSensitive` / `acceptSpellingErrors` setters, and no preservation of them either:
  // matching is always case-insensitive and never tolerates a typo, and a derived `blanks` cannot
  // carry a per-index flag safely (see the `Blank` schema comment — this deliberately overrides
  // B9's round-trip half).

  return {
    refInlineText,
    setInlineText,
  }
}

// --- drag_the_words --------------------------------------------------------

interface DistractorItem {
  index: number
  position: number
  value: string
}

interface UseInteractionDragTheWordsReturn {
  refText: ComputedRef<string>
  refMarkedWords: ComputedRef<MarkedWordItem[]>
  refDistractors: ComputedRef<DistractorItem[]>
  setText: (value: string) => void
  setDistractor: (index: number, value: string) => void
  addDistractor: () => void
  removeDistractor: (index: number) => void
}

export const useInteractionDragTheWords = (
  { screenId, activityId, interaction }: UseInteractionEditorOptions,
): UseInteractionDragTheWordsReturn => {
  const store = useAssessmentEditorStore()

  const refInteraction = computed(() => narrow(interaction(), 'drag_the_words'))

  const refText = computed<string>(() => refInteraction.value?.text ?? '')

  /** The marked `*word*` gaps, in authored order — the answer key spelled out under the counter. */
  const refMarkedWords = computed<MarkedWordItem[]>(() =>
    markedWords(refText.value, DRAG_GAP_REGEX, 1),
  )

  const refRawDistractors = computed<string[]>(() => refInteraction.value?.distractors ?? [])

  const refDistractors = computed<DistractorItem[]>(() =>
    refRawDistractors.value.map((value, index) => ({ index, position: index + 1, value })),
  )

  const setText = (value: string): void => {
    store.updateDragTheWords(screenId(), activityId(), { text: value })
  }

  /** An emptied list passes `null` — deleting the optional key — rather than storing `[]`. The
   *  store applies the same rule; this keeps the intent legible at the call site. */
  const writeDistractors = (next: string[]): void => {
    store.updateDragTheWords(screenId(), activityId(), {
      distractors: next.length > 0 ? next : null,
    })
  }

  const setDistractor = (index: number, value: string): void => {
    writeDistractors(refRawDistractors.value.map((item, position) =>
      position === index ? value : item,
    ))
  }

  const addDistractor = (): void => {
    writeDistractors([...refRawDistractors.value, ''])
  }

  const removeDistractor = (index: number): void => {
    writeDistractors(refRawDistractors.value.filter((_, position) => position !== index))
  }

  return {
    refText,
    refMarkedWords,
    refDistractors,
    setText,
    setDistractor,
    addDistractor,
    removeDistractor,
  }
}

// --- whiteboard ------------------------------------------------------------

interface UseInteractionWhiteboardReturn {
  refBackgroundUrl: ComputedRef<string>
  /** READ-ONLY: whatever alt a stored definition carries, so the uploader can label its preview.
   *  There is no setter — the editor offers no "Background description" field. */
  refBackgroundAlt: ComputedRef<string>
  setBackgroundUrl: (value: string) => void
}

export const useInteractionWhiteboard = (
  { screenId, activityId, interaction }: UseInteractionEditorOptions,
): UseInteractionWhiteboardReturn => {
  const store = useAssessmentEditorStore()

  const refInteraction = computed(() => narrow(interaction(), 'whiteboard'))

  const refBackgroundUrl = computed<string>(() =>
    refInteraction.value?.backgroundAsset?.url ?? '',
  )

  const refBackgroundAlt = computed<string>(() => refInteraction.value?.backgroundAlt ?? '')

  /** A plain URL (B4) — the media path is not in this run, so there is no uploader and no file
   *  picker here. `backgroundAsset` is `{ url }`, so one drops in later without migrating a stored
   *  definition. */
  const setBackgroundUrl = (value: string): void => {
    store.updateWhiteboard(screenId(), activityId(), {
      backgroundUrl: value.trim() ? value : null,
    })
  }

  return {
    refBackgroundUrl,
    refBackgroundAlt,
    setBackgroundUrl,
  }
}

// --- open_ended ------------------------------------------------------------

interface OpenEndedFormatItem {
  format: OpenEndedFormat
  label: string
  checked: boolean
}

interface UseInteractionOpenEndedReturn {
  /** The effective list: an absent `formats` reads as text only, an emptied one stays empty. */
  refFormats: ComputedRef<OpenEndedFormat[]>
  refFormatItems: ComputedRef<OpenEndedFormatItem[]>
  refHasNoFormat: ComputedRef<boolean>
  toggleFormat: (format: OpenEndedFormat) => void
}

const FORMAT_LABELS: Record<OpenEndedFormat, string> = {
  text: 'Text',
  whiteboard: 'Whiteboard',
  photo: 'Photo',
}

export const useInteractionOpenEnded = (
  { screenId, activityId, interaction }: UseInteractionEditorOptions,
): UseInteractionOpenEndedReturn => {
  const store = useAssessmentEditorStore()

  const refInteraction = computed(() => narrow(interaction(), 'open_ended'))

  const refFormats = computed<OpenEndedFormat[]>(() =>
    [...(refInteraction.value?.formats ?? DEFAULT_OPEN_ENDED_FORMATS)],
  )

  const refFormatItems = computed<OpenEndedFormatItem[]>(() =>
    OPEN_ENDED_FORMATS.map(format => ({
      format,
      label: FORMAT_LABELS[format],
      checked: refFormats.value.includes(format),
    })),
  )

  const refHasNoFormat = computed<boolean>(() => refFormats.value.length === 0)

  /** Unticking the last format is allowed; the save rule reports it. Written in the fixed tab order,
   *  so the stored array reads the way the player renders it. */
  const toggleFormat = (format: OpenEndedFormat): void => {
    const current = refFormats.value
    const next = current.includes(format)
      ? current.filter(item => item !== format)
      : OPEN_ENDED_FORMATS.filter(item => item === format || current.includes(item))
    store.updateOpenEnded(screenId(), activityId(), { formats: next })
  }

  return {
    refFormats,
    refFormatItems,
    refHasNoFormat,
    toggleFormat,
  }
}
